import styles from "../styles/Layout/Sidebar.module.css";
import React, { useState, useEffect, useContext } from 'react';
import { NavLink } from "react-router-dom";
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PaymentIcon from '@mui/icons-material/Payment';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import PublicIcon from '@mui/icons-material/Public';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EmailIcon from '@mui/icons-material/Email';
import TelegramIcon from '@mui/icons-material/Telegram';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { SettingsModal } from "../components/UI/Custom/SettingsModal";
import { LangContext } from "../context/LangContext";
import { ThemeContext } from "../context/ThemeContext";
import { getMyAccountApi } from "../utils/api/Account/getMyAccountApi";
import { canManageTelegramNotifications } from "../utils/custom/telegramNotificationsAccess";
import { apiRequest } from "../utils/api/apiRequest";
import { isLoginAsActive, restoreMainSessionAfterLoginAs, clearLoginAsSession } from "../utils/custom/loginAsSession";

const translations = {
  ru: {
    leads: 'Лиды',
    apiDocs: 'API Документация',
    employees: 'Сотрудники',
    affiliates: 'Аффилиаторы',
    logout: 'Выйти',
    userRole: 'Роль пользователя',
    settings: 'Настройки',
    transactions: 'Транзакции',
    allowedIps: 'Разрешенные IP',
    depositMethods: 'Методы Депозита',
    contactUs: 'Запросы',
    telegramNotifications: 'Telegram',
    analytics: 'Аналитика',
    inProgress: 'In progress'
  },
  en: {
    leads: 'Leads',
    apiDocs: 'API Documentation',
    employees: 'Employees',
    affiliates: 'Affiliates',
    logout: 'Logout',
    userRole: 'User role',
    settings: 'Settings',
    transactions: 'Transactions',
    allowedIps: 'Allowed IPs',
    depositMethods: 'Deposit Methods',
    contactUs: 'Contact Us',
    telegramNotifications: 'Telegram',
    analytics: 'Analytics',
    inProgress: 'In progress'
  },
};

export const Sidebar = ({ setIsLogin, userRole, userData, tabsSelected, setTabsSelected }) => {
  const { langSelected } = useContext(LangContext);
  const { theme } = useContext(ThemeContext);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isIpAdmin, setIsIpAdmin] = useState(false);
  const [canManageTelegramSettings, setCanManageTelegramSettings] = useState(false);

  const { first_name = '', last_name = '' } = userData || {};

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const toggleSettingsModal = () => setIsSettingsModalOpen(!isSettingsModalOpen);
  const HAS_CONTACT_US_LIST_FEATURE = String(process.env.REACT_APP_HAS_CONTACT_US_LIST_FEATURE).toLowerCase() === 'true';

  const handleLeave = async () => {
    if (isLoginAsActive()) {
      try {
        await apiRequest('/admin/auth/logout', {
          method: 'POST',
          defaultErrorMessage: 'Failed to logout from login-as session',
          notifyOnError: false,
        });
      } catch (error) {
        console.error('Failed to revoke login-as token:', error);
      }

      const restoredLoginData = restoreMainSessionAfterLoginAs();

      if (restoredLoginData) {
        setIsLogin(restoredLoginData);
        window.location.reload();
        return;
      }
    }

    clearLoginAsSession();
    localStorage.removeItem('login');
    localStorage.removeItem('token');
    localStorage.removeItem('admin_id');
    localStorage.removeItem('leadsItemsPerPage');
    setIsLogin({ isLoggedIn: false, userData: null });
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 601) {
        setIsMobileMenuOpen(false);
      }
      if (window.innerWidth <= 1300 && window.innerWidth > 600) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const fetchSecurityAccess = async () => {
      try {
        const accountData = await getMyAccountApi();
        if (accountData && accountData.ip_admin !== undefined) {
          setIsIpAdmin(accountData.ip_admin);
        }

        setCanManageTelegramSettings(canManageTelegramNotifications(accountData));
      } catch (error) {
        console.error("Failed to fetch account data for security checks:", error);
        setIsIpAdmin(false);
        setCanManageTelegramSettings(false);
      }
    };

    if (userRole === "Admin") {
      fetchSecurityAccess();
    } else {
      setIsIpAdmin(false);
      setCanManageTelegramSettings(false);
    }
  }, [userRole]);

  const currentSidebarClass = `${styles.sidebarContainer} ${isMobileMenuOpen ? styles.open : ''} ${!isMobileMenuOpen && isCollapsed ? styles.collapsed : ''} ${theme === 'dark' ? styles.darkSidebar : ''}`;
  const showText = !isCollapsed || isMobileMenuOpen;
  const currentTranslation = translations[langSelected] || translations.ru;

  return (
    <>
      <div className={`${styles.mobileMenuButton} ${theme === 'dark' ? styles.darkMobileButton : ''}`} onClick={toggleMobileMenu}>
        {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
      </div>
      <div className={currentSidebarClass}>
        <div className={styles.section}>
          <div className={styles.userProfile}>
            <NavLink to={"/account"} className={({ isActive }) => isActive ? `${styles.userProfileLink} ${styles.userNameActive}` : styles.userProfileLink}>
              <AccountCircleIcon className={styles.userAvatarIcon} />
              {showText && (
                <>
                  <h3 className={styles.userName}>
                    {`${first_name} ${last_name}`}
                  </h3>
                  <p className={styles.userRole}>{`${userRole}`}</p>
                </>
              )}
            </NavLink>
          </div>
          <nav className={styles.navigation}>
            <ul className={styles.navList}>
              {userRole === "Affiliator" && (
                <>
                  <li className={styles.navItem}>
                    <NavLink to={"/lead"} className={({ isActive }) => isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink}>
                      <DashboardIcon className={styles.navIcon} />
                      {showText && currentTranslation.leads}
                    </NavLink>
                  </li>
                  <li className={styles.navItem}>
                    <NavLink to={"/documentation"} className={({ isActive }) => isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink}>
                      <DescriptionIcon className={styles.navIcon} />
                      {showText && currentTranslation.apiDocs}
                    </NavLink>
                  </li>
                </>
              )}
              {userRole === "Admin" && (
                  <>
                    <li className={styles.navItem}>
                      <NavLink to={"/lead"}
                               className={({isActive}) => isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink}>
                        <DashboardIcon className={styles.navIcon}/>
                        {showText && currentTranslation.leads}
                      </NavLink>
                    </li>
                    <li className={styles.navItem}>
                      <NavLink to={"/employees"}
                               className={({isActive}) => isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink}>
                        <PeopleIcon className={styles.navIcon}/>
                        {showText && currentTranslation.employees}
                      </NavLink>
                    </li>
                    <li className={styles.navItem}>
                      <NavLink to={"/other"}
                               className={({isActive}) => isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink}>
                        <GroupAddIcon className={styles.navIcon}/>
                        {showText && currentTranslation.affiliates}
                      </NavLink>
                    </li>
                    <li className={styles.navItem}>
                      <NavLink to={"/deposit-methods"}
                               className={({isActive}) => isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink}>
                        <CreditCardIcon className={styles.navIcon}/>
                        {showText && currentTranslation.depositMethods}
                      </NavLink>
                    </li>
                    {HAS_CONTACT_US_LIST_FEATURE && (
                      <li className={styles.navItem}>
                        <NavLink to={"/contact-us"}
                                 className={({isActive}) => isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink}>
                          <EmailIcon className={styles.navIcon}/>
                          {showText && currentTranslation.contactUs}
                        </NavLink>
                      </li>
                    )}
                    {isIpAdmin && (
                        <li className={styles.navItem}>
                          <NavLink to={"/allowed-ips"}
                                   className={({isActive}) => isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink}>
                            <PublicIcon className={styles.navIcon}/>
                            {showText && currentTranslation.allowedIps}
                          </NavLink>
                        </li>
                    )}
                    {canManageTelegramSettings && (
                        <li className={styles.navItem}>
                          <NavLink to={"/telegram-notifications"}
                                   className={({isActive}) => isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink}>
                            <TelegramIcon className={styles.navIcon}/>
                            {showText && currentTranslation.telegramNotifications}
                          </NavLink>
                        </li>
                    )}
                    <li className={styles.navItem}>
                      <div
                        className={`${styles.navLink} ${styles.navLinkDisabled}`}
                        title={currentTranslation.inProgress}
                        aria-disabled="true"
                      >
                        <QueryStatsIcon className={styles.navIcon}/>
                        {showText && currentTranslation.analytics}
                      </div>
                    </li>
                    <li className={styles.navItem}>
                      <NavLink to={"/transactions/all"}
                               className={({isActive}) => isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink}>
                        <PaymentIcon className={styles.navIcon}/>
                        {showText && currentTranslation.transactions}
                      </NavLink>
                    </li>
                  </>
              )}
              <li className={styles.navItem}>
                <button
                    onClick={toggleSettingsModal}
                    className={styles.navLink}
                >
                  <SettingsIcon className={styles.navIcon}/>
                  {showText && currentTranslation.settings}
                </button>
              </li>
            </ul>
          </nav>
        </div>
        <div className={styles.sidebarFooter}>
          <button onClick={handleLeave} className={styles.logoutButton}>
            <LogoutIcon className={styles.logoutIcon} />
            {showText && currentTranslation.logout}
          </button>
        </div>
      </div>
      {isSettingsModalOpen && <SettingsModal onClose={toggleSettingsModal} />}
    </>
  );
};
